import {
  DropdownGroup,
  NullableDropdownGroup,
  DropdownOption,
  NullableDropdownItem,
  DropdownItem,
} from './types';

export function isDropdownGroup(object: any): object is DropdownGroup {
  return 'items' in object && Array.isArray(object.items) && object.items.length;
}

export function isNullableDropdownGroup(object: any): object is NullableDropdownGroup {
  return 'items' in object && Array.isArray(object.items);
}

export function isSelectableDropdownOption(object: any): object is DropdownOption {
  return 'value' in object;
}

export function convertNullableGroupToDropdownGroup(
  group: NullableDropdownGroup
): DropdownGroup | undefined {
  const items = group.items.filter(item => !!item) as DropdownOption[];
  return items.length > 0 ? { ...group, items } : undefined;
}

export function filterNullableDropdownItems(items: NullableDropdownItem[]): DropdownItem[] {
  return items
    .map(
      item =>
        item && (isNullableDropdownGroup(item) ? convertNullableGroupToDropdownGroup(item) : item)
    )
    .filter(item => !!item) as DropdownItem[];
}

export function findSelectedItem(items: DropdownItem[], lookInGroups = true) {
  let selectedItem: DropdownOption | undefined;

  for (const item of items) {
    if (isDropdownGroup(item)) {
      if (lookInGroups) {
        selectedItem = findSelectedItem(item.items, false);
      }
    } else if (item.selected) {
      selectedItem = item;
    }

    if (selectedItem) {
      break;
    }
  }

  return selectedItem;
}

export function updateSelectionByValue(
  value: string | number | undefined,
  items: NullableDropdownItem[],
  lookInGroups = true
) {
  if (!value) {
    return items;
  }

  const updatedItems: any[] = [];

  for (const item of items) {
    if (isDropdownGroup(item)) {
      updatedItems.push({
        items: updateSelectionByValue(value, item.items, false),
      });
    } else {
      if (item && isSelectableDropdownOption(item)) {
        item.selected = item.value === value;
      }
      updatedItems.push(item);
    }
  }

  return updatedItems;
}

export function findItemByValue(
  value: string | number | undefined,
  items: DropdownItem[],
  lookInGroups = true
) {
  if (value === undefined) {
    return undefined;
  }

  let matchingItem: DropdownOption | undefined;

  for (const item of items) {
    if (isDropdownGroup(item)) {
      if (lookInGroups) {
        matchingItem = findItemByValue(value, item.items, false);
      }
    } else if (getItemValue(item) === value) {
      matchingItem = item;
    }

    if (matchingItem) {
      break;
    }
  }

  return matchingItem;
}

export function getItemValue(item: DropdownOption) {
  return item.value !== undefined ? item.value : item.link;
}

export function getSelectedItemByValue(value: string | number | undefined, items: DropdownItem[]) {
  if (value !== undefined) {
    return getFlatItemList(items).find(item => getItemValue(item) === value);
  }
  return undefined;
}

export function hasSubmenus(items: NullableDropdownItem[]) {
  return getFlatItemList(filterNullableDropdownItems(items)).some(item => !!item.submenu);
}

function getFlatItemList(items: DropdownItem[]) {
  let flatItems: DropdownOption[] = [];

  items.forEach(item => {
    if (isDropdownGroup(item)) {
      flatItems = flatItems.concat(item.items);
    } else {
      flatItems = flatItems.concat([item]);
    }
  });

  return flatItems;
}
